import { UnknownFail } from '@obvious.tech/util/Fail'
import { useDebounce } from '@obvious.tech/util/react/hooks'
import { err, Result, ResultAsync } from 'neverthrow'
import { useDataProvider } from 'ra-core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getEntityById } from 'src/api/getEntityById'
import { TReferenceTarget } from 'src/types'
import { Abortable } from '@obvious.tech/util/async'
import {
  TEntityUntagged,
  TEntityName,
  Entity,
} from '@obvious.tech/constellation/Entity'
import { surround } from '@obvious.tech/util/string'
import { identity } from '@obvious.tech/util/function'

export type TUseReferenceAutocompleteParam<
  Target extends TEntityName = TEntityName,
> = TReferenceTarget<Target>

export type TUseReferenceAutocompleteReturn<
  Target extends TEntityName = TEntityName,
  _Target extends TEntityUntagged = TEntityUntagged<Target>,
> = {
  entities: Result<Array<TEntityUntagged<Target>>, UnknownFail>
  entitiesLoading: boolean
  search: (query: string) => Promise<void>
  getById: (id: string) => ResultAsync<_Target, UnknownFail>
}

export const useReferenceAutocomplete = <
  Target extends TEntityName = TEntityName,
>({
  target,
  targetIdKeyPath,
  targetLabelKeyPath,
}: TUseReferenceAutocompleteParam<Target>): TUseReferenceAutocompleteReturn<Target> => {
  const dataProvider = useDataProvider()
  const [entities, setEntities] = useState<
    Result<Array<TEntityUntagged<Target>>, UnknownFail>
  >(err(new UnknownFail('not loaded')))

  const [entitiesLoading, setEntitiesLoading] = useState(true)

  const getById = useCallback(
    (id: string) => {
      return getEntityById({
        dataProvider,
        id,
        entity: target,
      })
    },
    [dataProvider, target],
  )

  const search = useCallback(
    ([query]: [query: string]): ResultAsync<
      Array<TEntityUntagged<Target>>,
      UnknownFail
    > => {
      const resource = Entity.getResource(target)
      const sortField = targetLabelKeyPath ?? targetIdKeyPath ?? 'id'

      const filterFields = [targetIdKeyPath ?? 'id', targetLabelKeyPath].filter(
        it => typeof it === 'string',
      )

      const addBrackets =
        filterFields.length > 0 ? surround(['(', ')']) : identity<string>
      const filterField = addBrackets(filterFields.join('|'))

      const result = ResultAsync.fromThrowable(
        async (
          ...args: Parameters<(typeof dataProvider)['getManyReference']>
        ): Promise<Array<TEntityUntagged<Target>>> => {
          const response = await dataProvider.getManyReference<
            TEntityUntagged<Target>
          >(...args)
          return response.data
        },
        UnknownFail.from,
      )(resource, {
        // @ts-expect-error handled in DataProvider
        pagination: {},
        sort: {
          field: sortField,
          order: 'ASC',
        },
        filter: {
          [filterField]: query,
        },
      })

      return result
    },
    [dataProvider, target, targetIdKeyPath, targetLabelKeyPath],
  )
  const debouncedSearch = useDebounce(search)

  useEffect(() => {
    const controller = new AbortController()
    void Abortable.match(Abortable.from(search(['']), controller.signal), {
      onContinue: setEntities,
      onAbort() {},
      onFinally() {
        setEntitiesLoading(false)
      },
    })

    return () => controller.abort()
  }, [search])

  return useMemo(
    () => ({
      search: async (query: string): Promise<void> => {
        setEntitiesLoading(true)
        await Abortable.match(debouncedSearch.call(query), {
          onContinue(entities) {
            setEntities(entities)
          },
          onAbort() {},
          onFinally() {
            setEntitiesLoading(false)
          },
        })
      },
      entities,
      entitiesLoading,
      getById,
    }),
    [debouncedSearch, getById, entities, entitiesLoading],
  )
}
